import {DataEventContextName} from './dataEventContexts';
import {ActivationDataEventObject} from './objectNames/activationDataEventObject';
import {AdminSetupGuideDataEventObject} from './objectNames/adminSetupGuideDataEventObject';
import {AdminSuggestionsDataEventObject} from './objectNames/adminSuggestionsDataEventObject';
import {AnalyticsDataEventObject} from './objectNames/analyticsDataEventObject';
import {AppDataEventObject} from './objectNames/appDataEventObject';
import {AppOpenDataEventObject} from './objectNames/appOpenDataEventObject';
import {AskCopilotDataEventObject} from './objectNames/askCopilotDataEventObject';
import {CalendarDataEventObject} from './objectNames/calendarDataEventObject';
import {CellsDataEventObject} from './objectNames/cellsDataEventObject';
import {
  CommentComposerDataEventObject,
  MessageComposerDataEventObject,
} from './objectNames/composerDataEventObject';
import {ContactsDataEventObject} from './objectNames/contactsDataEventObject';
import {ConversationDataEventObject} from './objectNames/conversationDataEventObject';
import {ConversationSummaryDataEventObject} from './objectNames/conversationSummaryDataEventObject';
import {CopilotSuggestedKnowledgeDataEventObject} from './objectNames/copilotSuggestedKnowledgeDataEventObject';
import {FluSetupGuideDataEventObject} from './objectNames/fluSetupGuideDataEventObject';
import {GenericDataEventObject} from './objectNames/genericDataEventObject';
import {InboxRedesignDataEventObjectsEnum} from './objectNames/inboxRedesignDataEventObject';
import {KnowledgeBaseDataEventObject} from './objectNames/knowledgeBaseDataEventObject';
import {LinkedConversationsDataEventObject} from './objectNames/linkedConversationsDataEventObject';
import {MacrosDataEventObject} from './objectNames/macrosDataEventObject';
import {OnboardingDataEventObject} from './objectNames/onboardingDataEventObject';
import {PlanPurchaseDataEventObjectsEnum} from './objectNames/planPurchaseDataEventObjects';
import {ResourceCopilotDataEventObjectsEnum} from './objectNames/resourceCopilotDataEventObject';
import {SearchDataEventObject} from './objectNames/searchDataEventObject';
import {SettingsDataEventObjects} from './objectNames/settingsDataEventObject';
import {SideFrameDataEventObject} from './objectNames/sideFrameDataEventObject';
import {SystemNotificationDataEventObject} from './objectNames/systemNotificationDataEventObject';
import {TaskComposerDataEventObjectsEnum} from './objectNames/taskComposerDataEventObject';
import {TelephonyDataEventObject} from './objectNames/telephonyDataEventObject';
import {TopBarDataEventObject} from './objectNames/topBarDataEventObject';
import {ViewerDataEventObject} from './objectNames/viewerDataEventObject';
import {WorkspacesDataEventObject} from './objectNames/workspacesDataEventObject';

type DataEventContextNameWithGeneric = DataEventContextName | 'generic';

/**
 * Map of all contexts to their corresponding event object names.
 */
const DataObjectNameMap = {
  // Generic object names used across several contexts.
  generic: GenericDataEventObject,

  p0: TopBarDataEventObject,
  p1: WorkspacesDataEventObject,
  p2: CellsDataEventObject,
  p3: ViewerDataEventObject,
  p4: SideFrameDataEventObject,
  bottom_bar: OnboardingDataEventObject,

  search: SearchDataEventObject,

  comment_composer: CommentComposerDataEventObject,
  conversation_summary: ConversationSummaryDataEventObject,
  message_composer: MessageComposerDataEventObject,

  admin_setup_guide: AdminSetupGuideDataEventObject,
  flu_setup_guide: FluSetupGuideDataEventObject,
  analytics: AnalyticsDataEventObject,
  calendar: CalendarDataEventObject,
  accounts_manager: ContactsDataEventObject,
  settings: SettingsDataEventObjects,
  kb: KnowledgeBaseDataEventObject,
  getting_started: OnboardingDataEventObject,
  activation: ActivationDataEventObject,
  app: AppDataEventObject,
  app_open: AppOpenDataEventObject,
  system_notification: SystemNotificationDataEventObject,
  linked_conversations: LinkedConversationsDataEventObject,
  macros: MacrosDataEventObject,
  admin_suggestions: AdminSuggestionsDataEventObject,
  dialer: TelephonyDataEventObject,
  call_status_banner: TelephonyDataEventObject,
  inbox_redesign: InboxRedesignDataEventObjectsEnum,
  inbox_improvements_toggle: InboxRedesignDataEventObjectsEnum,
  task_composer: TaskComposerDataEventObjectsEnum,
  ask_copilot: AskCopilotDataEventObject,
  copilot_suggested_knowledge: CopilotSuggestedKnowledgeDataEventObject,

  plan_purchase_popup: PlanPurchaseDataEventObjectsEnum,
  left_panel: TopBarDataEventObject,
  conversation: ConversationDataEventObject,
  resource_copilot: ResourceCopilotDataEventObjectsEnum,
  // When adding a new context, add the corresponding object name enum here.
} as const satisfies Record<DataEventContextNameWithGeneric, {[key: string]: string}>;

type RecordValue<T> = T[keyof T];

/**
 *  Union of all the possible data tracking event object names.
 *  If you need to add an event, add it to the corresponding object name enum above.
 */
export type DataEventObjectName = RecordValue<{
  [K in keyof typeof DataObjectNameMap]: RecordValue<(typeof DataObjectNameMap)[K]>;
}>;

const dataObjectNameSet = new Set(
  Object.values(DataObjectNameMap).flatMap((objectNames) => Object.values(objectNames)),
);
export function isDataEventObjectName(value: string): value is DataEventObjectName {
  return dataObjectNameSet.has(value);
}
